import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LOCALE_DIR, getLocale, onLocale, otherLocale, setLocale as write, type Locale } from '../lib/locale';
import { stringsFor, type Strings } from '../lib/strings';

/**
 * The language, as React sees it.
 *
 * The decision itself lives in lib/locale.ts, which is a module rather than a
 * hook because the document's `dir` has to be set before React exists and
 * because half the sentences in the product are built by plain functions. This
 * file is the other half: a context so that changing the language re-renders
 * everything that says anything, and a `useStrings` that hands a component the
 * catalogue rather than a lookup function.
 *
 * There is exactly one provider, at the root. It subscribes to the module and
 * mirrors it into state — so a `setLocale` called from anywhere, including
 * from outside React, still repaints the tree.
 */

interface LocaleValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  strings: Strings;
  setLocale(locale: Locale): void;
  /** Flip to the other language. A two-language product's control is a switch. */
  toggle(): void;
}

const Ctx = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(getLocale);

  // The module is the source of truth, not this state. Subscribing rather
  // than owning is what lets `setLocale` be called from a plain module — or
  // from a second provider in a test — without the tree going stale.
  useEffect(() => onLocale(setState), []);

  const setLocale = useCallback((next: Locale) => write(next), []);
  const toggle = useCallback(() => write(otherLocale()), []);

  const value = useMemo<LocaleValue>(
    () => ({ locale, dir: LOCALE_DIR[locale], strings: stringsFor(locale), setLocale, toggle }),
    [locale, setLocale, toggle],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function use(): LocaleValue {
  const value = useContext(Ctx);
  // A hard failure rather than a silent English fallback. A component reading
  // strings outside the provider would render the whole interface in the
  // wrong language and look like a translation bug for as long as it took
  // somebody to find it; this says where to look.
  if (!value) throw new Error('useLocale/useStrings used outside <LocaleProvider>');
  return value;
}

/** Everything the interface says, in the language it is currently in. */
export function useStrings(): Strings {
  return use().strings;
}

/** The language itself, for the switch and for the few places that must branch on direction. */
export function useLocale(): LocaleValue {
  return use();
}
